import { isRejected } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { localStorage } from 'utils'
import { LiveChatThread } from 'apps/chat/constants'
import {
  FilterTypes,
  ThreadsSortingTypes,
  StatusFilterType,
  Filter,
  ConversationFilterType,
  LiveChatBulkActionTypes,
  ALL_TEAM_MEMBERS_ID,
} from 'apps/chat/constants/LiveChatInterfaces'
import * as atypes from 'apps/chat/constants/LiveChatReduxActionTypes'
import { setSelectedLabelId } from 'apps/chat/redux/labelsSlice'
import {
  getThreadsFilterData,
  getThreadsStatusFilter,
  getFolder,
  getFolderUserId,
  getThreadStatusesByStatusFilterAndFolder,
  getSelectedThreads,
  getThreadsSelectionType,
  getTeamMemberFilter,
  getThreads,
} from 'apps/chat/selectors/threadSelectors'
import * as atypesAudience from 'common/audience/constants/AudienceReduxActionTypes'
import { alert } from 'common/core'
import { ChannelType } from 'common/core/constants/ChannelType'
import { IAsyncThunkAction, IThunkAction } from 'common/core/interfaces/actions'
import { FilterCondition, FilterGroupItem, Filter as FilterType } from 'common/filter/interfaces'
import { chatApi } from 'shared/api/requests/chat'
import { createAsyncAction } from 'shared/lib/redux'
import { isRequestError } from 'utils/api/mcApi/RequestError'
import { navigatePollyfill } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'
import { linkURL } from 'utils/url'

import { LiveChatLocalStorageIds } from '../../constants/LiveChatLocalStorageIds'
import { getAutomationPauseEndCaption } from '../../helpers/pauseAutomationsHelpers'
import { ThreadStatus } from '../../interfaces/statuses'
import { threadActions as actions, NAMESPACE } from '../../reducers/slices/threadListSlice'
import {
  isFilterChannel,
  isFilterConversations,
  isFilterLabel,
  isFilterStatus,
  isFilterTagsAndCufs,
  isFilterTeamMember,
} from '../typeguards'

import { applyChangesToThreads } from './utils'

export const filterThreads = <T extends FilterGroupItem | FilterCondition = FilterGroupItem>(
  filter: FilterTypes,
  value: FilterType<T> | StatusFilterType | null,
): IThunkAction => {
  return (dispatch) => {
    dispatch(actions.dropThreadsList())

    if (isFilterStatus(filter, value)) {
      dispatch(actions.updateStatusFilter(value))
    }
    if (isFilterTagsAndCufs(filter, value)) {
      dispatch(actions.updateTagsAndCufsFilter(value))
    }

    dispatch(actions.fetchThreads())
  }
}

export const updateFilters = (
  filterType: Filter,
  value: ConversationFilterType | ChannelType | number | null,
): IThunkAction => {
  return (dispatch) => {
    dispatch(actions.dropThreadsList())

    if (isFilterConversations(filterType, value)) {
      localStorage.setItem(LiveChatLocalStorageIds.CONVERSATIONS_FILTER, value)
      localStorage.removeItem(LiveChatLocalStorageIds.LABEL_FILTER)
      localStorage.removeItem(LiveChatLocalStorageIds.TEAM_MEMBER_FILTER)

      dispatch(actions.updateConversationsFilter(value))
      dispatch(actions.updateStatusFilter(StatusFilterType.OPEN))
      dispatch(setSelectedLabelId(null))
    } else if (isFilterTeamMember(filterType, value)) {
      localStorage.setItem(LiveChatLocalStorageIds.TEAM_MEMBER_FILTER, value)
      localStorage.removeItem(LiveChatLocalStorageIds.LABEL_FILTER)

      localStorage.removeItem(LiveChatLocalStorageIds.CONVERSATIONS_FILTER)
      dispatch(actions.updateConversationsFilter(ConversationFilterType.TEAM))
      dispatch(setSelectedLabelId(null))
      dispatch(actions.updateTeamMembersFilter(value))
    } else if (isFilterChannel(filterType, value)) {
      dispatch(actions.updateChannelsFilter(value))
    } else if (isFilterLabel(filterType, value)) {
      localStorage.removeItem(LiveChatLocalStorageIds.CONVERSATIONS_FILTER)
      localStorage.removeItem(LiveChatLocalStorageIds.TEAM_MEMBER_FILTER)
      localStorage.setItem(LiveChatLocalStorageIds.LABEL_FILTER, value)

      dispatch(setSelectedLabelId(value))
      dispatch(actions.updateConversationsFilter(ConversationFilterType.ALL))
    }

    dispatch(actions.fetchThreads())
  }
}

export const sortThreads = (sorting: ThreadsSortingTypes): IThunkAction => {
  return (dispatch) => {
    if ([ThreadsSortingTypes.NEWEST, ThreadsSortingTypes.OLDEST].includes(sorting)) {
      dispatch(actions.dropThreadsList())
      dispatch(actions.updateThreadsSorting(sorting))
      dispatch(actions.fetchThreads())
    }
  }
}

export const performBulkAction = createAsyncAction(
  `${NAMESPACE}/performBulkAction`,
  async ({ type, uuid }: { type: LiveChatBulkActionTypes; uuid: string }, { getState }) => {
    const state = getState()
    const ids = Object.values(getSelectedThreads(state)).map((thread) => thread.id)
    const assignee = state.chat.threads.assignee

    const body = {
      type,
      ids,
      selection_type: getThreadsSelectionType(state),
      filter: getThreadsFilterData(state),
      status: getThreadsStatusFilter(state),
      folder: getFolder(state),
      folder_user_id: getFolderUserId(state),
      ...(uuid && { async: uuid }),
      ...(assignee && { assigned_user_id: assignee }),
    }

    const { data } = await chatApi.threads.bulk({ body })

    if (!data.async) {
      alert(l.translate('Done!'), 'success')
    }

    return data
  },
  {
    autoHandleByGuards: [chatApi.threads.bulk.isBulkActionInProgressError],
  },
)

// TODO: migrate to the new API
export const fetchInstagramStory = (link: string): IAsyncThunkAction => {
  return async (dispatch) => {
    try {
      return await dispatch({
        type: atypes.THREADS_INSTAGRAM_STORY_FETCH,
        $fetch: [link, { cache: 'no-store' }],
        $responseType: 'blob',
      })
    } catch (error) {
      if (isRequestError(error) && error.reason === 'business-logic') return
      errorTrackingService.logError(error, {
        scope: 'livechat',
        section: 'thread',
        fingerprint: 'fetch-instagram-story',
      })
      return
    }
  }
}

export const closeAllThreads = createAsyncAction(
  `${NAMESPACE}/closeAllThreads`,
  async (_, { dispatch }) => {
    const { data } = await chatApi.threads.closeAllThreads()

    dispatch(
      applyChangesToThreads(data.thread_list, {
        lc_thread_status: ThreadStatus.CLOSED,
      }),
    )
  },
  {
    autoHandleByGuards: [chatApi.threads.closeAllThreads.isBulkActionInProgressError],
  },
)

export function getNextThreadId(
  threads: LiveChatThread[],
  currentThreadId: LiveChatThread['id'],
  initialThreadIndex: number,
) {
  const threadIndex = threads.findIndex((thread) => thread.id === currentThreadId)
  const wasThreadFilteredOut = threadIndex === -1
  let nextThreadIndex

  if (wasThreadFilteredOut) {
    nextThreadIndex = Math.min(initialThreadIndex, threads.length)
  } else if (initialThreadIndex === threads.length - 1) {
    nextThreadIndex = initialThreadIndex - 1
  } else {
    nextThreadIndex = initialThreadIndex + 1
  }

  const safeNextThreadIndex = Math.min(nextThreadIndex, threads.length)

  return threads[safeNextThreadIndex]?.id
}

export const updateThreadStatusAndSelectNextThread = (
  threadId: string,
  status: ThreadStatus,
): IAsyncThunkAction => {
  return async (dispatch, getState) => {
    const state = getState()

    const teamMembersFilter = getTeamMemberFilter(state)
    const threads = getThreads(state) || []
    const threadsStatuses = getThreadStatusesByStatusFilterAndFolder(state)
    const initialThreadIndex = threads.findIndex((thread) => thread.id === threadId)

    const response = await dispatch(actions.updateThreadStatus({ threadId, status }))
    if (!response || isRejected(response)) return

    let filteredThreads = threads

    if (!threadsStatuses.includes(status)) {
      filteredThreads = filteredThreads.filter((thread) => thread.id !== threadId)
    }

    if (teamMembersFilter && teamMembersFilter !== ALL_TEAM_MEMBERS_ID) {
      filteredThreads = filteredThreads.filter(
        (thread) => thread.assignment?.user_id === teamMembersFilter,
      )
    }

    filteredThreads = filteredThreads.filter((thread) =>
      threadsStatuses.includes(thread.lc_thread_status),
    )

    if (filteredThreads.length) {
      const nextThreadId = getNextThreadId(threads, threadId, initialThreadIndex)

      if (nextThreadId) {
        navigatePollyfill(linkURL(`/chat/${nextThreadId}`))
      }
    }

    return response
  }
}

export const setPauseForever = createAsyncAction(
  `${NAMESPACE}/setPauseForever`,
  async (threadId: string, { dispatch }) => {
    analyticsService.sendEvent('LIVE_CHAT.AUTOMATION_PAUSE_SET', { threadId, delay: 'forever' })
    analyticsService.sendEvent('LIVE_CHAT.PAUSE.CLICK')

    await chatApi.threads.setPauseForever({
      body: {
        user_id: threadId,
      },
    })

    alert(l.translate('Automations paused forever'))

    const subscriber = {
      ts_automation_paused_until: null,
      ts_automation_paused_forever: true,
    }
    dispatch({
      type: atypesAudience.AUDIENCE_UPDATE_CURRENT_SUBSCRIBER,
      subscriber,
    })
    dispatch({
      type: atypesAudience.AUDIENCE_UPDATE_PAUSE_AUTOMATION,
      id: threadId,
      subscriber,
    })
  },
  {
    autoHandleByGuards: [chatApi.threads.setPauseForever.isCurrentThreadUserError],
  },
)

export const setPause = createAsyncAction(
  `${NAMESPACE}/setPause`,
  async ({ threadId, delay }: { threadId: string; delay: number }, { dispatch }) => {
    analyticsService.sendEvent('LIVE_CHAT.AUTOMATION_PAUSE_SET', { threadId, delay })
    analyticsService.sendEvent('LIVE_CHAT.PAUSE.CLICK')

    const response = await chatApi.threads.setPause({
      body: {
        user_id: threadId,
        delay,
      },
    })

    const timeStamp = response.data.thread?.ts_automation_paused_until

    if (timeStamp) {
      alert(getAutomationPauseEndCaption(timeStamp))

      const subscriber = {
        ts_automation_paused_until: timeStamp,
        ts_automation_paused_forever: false,
      }
      dispatch({
        type: atypesAudience.AUDIENCE_UPDATE_CURRENT_SUBSCRIBER,
        subscriber,
      })
      dispatch({
        type: atypesAudience.AUDIENCE_UPDATE_PAUSE_AUTOMATION,
        id: threadId,
        subscriber,
      })
    }
  },
  {
    autoHandleByGuards: [chatApi.threads.setPause.isCurrentThreadUserError],
  },
)

export const clearPause = createAsyncAction(
  `${NAMESPACE}/clearPause`,
  async (threadId: string, { dispatch }) => {
    analyticsService.sendEvent('LIVE_CHAT.AUTOMATION_PAUSE_CLEARED', { threadId })

    await chatApi.threads.clearPause({
      body: {
        user_id: threadId,
      },
    })

    alert(l.translate('Automations resumed'))

    const subscriber = {
      ts_automation_paused_until: null,
      ts_automation_paused_forever: false,
    }
    dispatch({
      type: atypesAudience.AUDIENCE_UPDATE_CURRENT_SUBSCRIBER,
      subscriber,
    })
    dispatch({
      type: atypesAudience.AUDIENCE_UPDATE_PAUSE_AUTOMATION,
      id: threadId,
      subscriber,
    })
  },
  {
    autoHandleByGuards: [chatApi.threads.clearPause.isCurrentThreadUserError],
  },
)
